import { get, patch, post } from "helpers/api_helper"
import { invalidateEventCacheAfterSuccess } from "../../../../../eventCachePolicy"
import { entrySlot } from "../../components/participantIdentity"

const getCompetitionEventId = competition =>
  competition?.eventId || competition?.event?._id || competition?.event

const getCompetitionId = competition => competition?._id || competition?.id

// The text a Main Draw dialog shows when its match write was not sent; `kept` names
// what the dialog keeps ("inputs are", "selection is").
export const matchSendFailure = (error, kept) =>
  error?.response?.status === 401
    ? `Your session expired, so the change was not sent. Sign in again in a new tab, then save here; your ${kept} preserved.`
    : `The change could not be sent. Your ${kept} preserved; try again.`

export const getLogs = async (compId, filters, activePage) => {
  let response
  const formattedFilters = Object.keys(filters).map(filterKey => {
    switch (filterKey) {
      case "subscribeSL":
        return filters[filterKey] ? "subscribeSl=true" : ""
      case "subscribeML":
        return filters[filterKey] ? "subscribeMl=true" : ""
      case "subscribeWL":
        return filters[filterKey] ? "subscribeWl=true" : ""
      case "unsubscribeML":
        return filters[filterKey] ? "unsubscribeMl=true" : ""
      case "unsubscribeWL":
        return filters[filterKey] ? "unsubscribeWl=true" : ""
      case "unsubscribeSL":
        return filters[filterKey] ? "unsubscribeSl=true" : ""
      case "promote":
        return filters[filterKey] ? "promote=true" : ""
      case "reserve":
        return filters[filterKey] ? "reserve=true" : ""
      default:
        break
    }
  })
  try {
    const url = `/logs/entries`
    const params = new URLSearchParams(
      `?limit=40&page=${
        activePage + 1
      }&competitionId=${compId}&${formattedFilters.join("&")}`
    )
    response = await get(url, { params })
  } catch (error) {
    console.log(error)
  }
  return response
}

export const getMatches = competitionId => get(`/matches/${competitionId}`)
export const getMainDraw = competitionId => get(`/matches/${competitionId}/main-draw`)

// Save score, determine winner, update match data (was type=none)
export const updateMatchScore = async (matchId, compDetails, matchDetails, socketClientId) => {
  let response
  try {
    response = await patch(
      `/admin/matches/${matchId}`,
      { socketClientId, ...matchDetails, competitionId: compDetails._id }
    )
    invalidateEventCacheAfterSuccess(response, {
      eventId: getCompetitionEventId(compDetails),
      competitionId: getCompetitionId(compDetails),
    })
  } catch (error) {
    throw error
  }
  return response
}

// Replace entries across all matches in competition (was type=updateOnly)
export const replaceMatchEntries = async (matchId, compDetails, matchDetails, socketClientId) => {
  let response
  try {
    response = await patch(
      `/admin/matches/${matchId}/replace-entries`,
      { socketClientId, ...matchDetails, competitionId: compDetails._id }
    )
    invalidateEventCacheAfterSuccess(response, {
      eventId: getCompetitionEventId(compDetails),
      competitionId: getCompetitionId(compDetails),
    })
  } catch (error) {
    throw error
  }
  return response
}

// Swap entries between two phase matches (was type=swapEntries)
export const swapMatchEntries = async (matchId, compDetails, matchDetails, socketClientId) => {
  let response
  try {
    response = await patch(
      `/admin/matches/${matchId}/swap-entries`,
      { socketClientId, ...matchDetails, competitionId: compDetails._id }
    )
    invalidateEventCacheAfterSuccess(response, {
      eventId: getCompetitionEventId(compDetails),
      competitionId: getCompetitionId(compDetails),
    })
  } catch (error) {
    throw error
  }
  return response
}

export const createMatch = async (
  playerEntry,
  opponentEntry,
  event,
  competition,
  index,
  phaseLength,
  socketClientId
) => {
  const { gameType, drawModel, level, _id, phases } = competition

  // A Tenis10 child goes as the child, on its parent's account (participantIdentity.js, PRD 16 LR11c).
  const player = entrySlot(playerEntry)
  const playerPartner = playerEntry?.partner

  const opponent = entrySlot(opponentEntry)
  const opponentPartner = opponentEntry?.partner

  const phase = phases?.length > 0 ? phases[0] : null

  const payload = {
    matchType: "competition",
    competition: {
      gameType: phase?.gameType,
      position: index + 1, // ?
      drawType: phase?.drawModel,
      type: event?.category,
      stage: 1,
      level: level,
      phaseId: phase?._id,
      id: _id,
      eventId: event?._id,
      eventName: event?.name,
      finalStage: Math.log2(phase?.mlNumber),
      winner: null,
    },
    player: {
      ...player,
      seeded: false,
      score: [],
      competitionPoints: 0,
    },
    ...(playerPartner?.userId && {
      playerPartner: {
        ...playerPartner,
        score: [],
        competitionPoints: 0,
      },
    }),
    opponent: {
      ...opponent,
      score: [],
      competitionPoints: 0,
      seeded: false,
    },
    ...(opponentPartner?.userId && {
      opponentPartner: {
        ...opponentPartner,
        score: [],
        competitionPoints: 0,
      },
    }),
  }

  const response = await post(`/matches`, { ...payload, socketClientId })
  invalidateEventCacheAfterSuccess(response, {
    eventId: event?._id,
    competitionId: _id,
  })
  return response
}
